import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { Button } from 'ui/mobile-ds';
import {
  CashlessHospital,
  AllClaims,
  HealthId,
  CostOfAilment,
  JourneyIcon,
  CheckMyCoverage,
} from 'ui/icons';
import { ArrowForward } from 'ui/mobile-ds/icons';
import { twConfig } from 'ui/ds/utils/getConfig';
import get from 'lodash.get';
import AppConfig from '@/AppConfig';
import { Layout } from '@/components/Common/Layout';
import { useAppContext } from '@/context/AppContext';
import useBreakpoints from '@/hooks/useBreakpoints';
import {
  MutationState,
  useMutation,
  useMutationState,
  useQuery,
} from '@tanstack/react-query';
import { getClaimsCount } from '@/queries/claims.queries';
import { BlockShimmer } from '@/components/Common/Shimmer';
import { ConditionalLink } from '@/components/Common';
import { policyGPTEnabledQuery } from '@/queries/bot.queries';
import { useFloatingWidgetContext } from '@/context/FloatingWidgetContext';
import { MOBILE_DEEPLINKS } from '@/constants';
import { useEffect } from 'react';
import { getPlumAppUrl, redirectToPlumApp } from '@/utils';
import { getNetworkHospitalConfig } from '@/queries/networkHospital.queries';
import { getAllBenefitsForMember } from '@/queries/memberDetails.queries';
import Cookies from 'js-cookie';
import { EBenefitCategory, INetworkHospitalConfig } from '@/types';

const LINKS = {
  NETWORK_HOSPITALS: `${AppConfig.BASE_PATH}/network-hospitals`,
  HEALTH_IDS: getPlumAppUrl(`/benefits-revamp`),
  // `${AppConfig.BASE_PATH}/redirect?to=health-id`,
  COST_OF_AILMENTS: AppConfig.COST_OF_AILMENTS_URL,
  FILE_CASHLESS_CLAIM_ARTICLE:
    '/support/article/claims-cashless-how-to-start-claim/support-center-articles/5?category=claims&sub_category=claims-cashless&nodeId=claims-cashless-how-to-start-claim',
  FILE_REIMBURSEMENT_CLAIM_ARTICLE:
    '/support/article/claims-reimbursement-how-to-start-claim/support-center-articles/18?category=claims&sub_category=claims-reimbursement&nodeId=claims-reimbursement-how-to-start-claim',
  POLICY_GPT: `/redirect?redirectUrl=${encodeURIComponent(
    `${AppConfig.CHAT_FRAMEWORK_BASE_URL}/bot/policygptv2?parent=plum-app`,
  )}`,
  CREATE_CLAIM: `${AppConfig.BASE_PATH}/claims/create`,
  ALL_CLAIMS: `${AppConfig.BASE_PATH}/claims/all`,
};

const Homepage = () => {
  const { data: claimsCount, isLoading: isClaimsCountLoading } = useQuery({
    ...getClaimsCount(),
  });

  const { active, drafts } = claimsCount ?? {};
  const total = (active ?? 0) + (drafts ?? 0);
  const { isDesktop } = useBreakpoints();
  const { isWebView } = useAppContext();

  const { data: policyGPTStatus, isFetched: isPolicyGPTStatusFetched } =
    useQuery({
      ...policyGPTEnabledQuery(),
    });

  const {
    data: nhStatusQueryData,
    mutate: fetchHospitalConfig,
    status: nhFetchStatus,
  } = useMutation({
    ...getNetworkHospitalConfig(null, null),
  });

  const isNHStatusLoading = nhFetchStatus === 'pending';

  const { navigateIframe, setIsFloatingWidgetOpen } =
    useFloatingWidgetContext();

  useEffect(() => {
    if (isWebView) {
      window?.NativeBridge?.onLoadComplete?.();
    }
  }, [isWebView]);

  const {
    isFetched: areBenefitsFetched,
    isLoading: areBenefitsLoading,
    data: allBenefits,
  } = useQuery({
    queryKey: [
      ...getAllBenefitsForMember([
        EBenefitCategory.INSURANCE,
        EBenefitCategory.EXPENSE_MANAGEMENT,
      ]).queryKey,
      'persist',
    ],
    queryFn: async (...args) => {
      const benefits = await getAllBenefitsForMember([
        EBenefitCategory.INSURANCE,
        EBenefitCategory.EXPENSE_MANAGEMENT,
      ]).queryFn(...args);
      const gmcBenefit = benefits?.find(
        (benefit) =>
          benefit.benefitType === 'gmc' ||
          benefit.benefitType === 'gmc-parent-only',
      );

      const insurerHandle = gmcBenefit?.benefitProviderHandle ?? '';
      const tpaName = gmcBenefit?.tpaName ?? '';

      Cookies.set('nh-insurer-handle', insurerHandle);
      Cookies.set('nh-tpa', tpaName);
      fetchHospitalConfig({
        insurer: insurerHandle,
        tpa: tpaName,
      });
      return {
        benefits,
        gmcBenefitId: gmcBenefit?.id,
      };
    },
  });

  const mutationState = useMutationState<
    MutationState<INetworkHospitalConfig, Error, unknown, unknown>
  >({
    filters: {
      mutationKey: getNetworkHospitalConfig(null, null).mutationKey,
      status: 'success',
    },
  });

  const latestMutation = mutationState?.[mutationState?.length - 1];

  const nhStatus =
    nhFetchStatus === 'idle' ? latestMutation?.data : nhStatusQueryData;

  const { gmcBenefitId } = allBenefits ?? {};

  const showSkeletonLoader = areBenefitsLoading || isNHStatusLoading;

  return (
    <Layout.Main
      fullWidth
      rootClassName={clsx({
        'bg-contain bg-no-repeat !bg-secondary-50': true,
        'bg-claims-hero !bg-mobile-hero-600 !bg-fixed': isWebView,
      })}
      className="px-0 md:px-5 lg:px-0"
      containerProps={{
        className: clsx({
          'h-full z-[1] relative': isWebView,
        }),
      }}
      headerProps={{
        title: 'Claims',
        description: 'File new claims and track the status of an existing one',
        theme: isWebView ? 'dark' : 'light',
        bg: 'bg-transparent',
        className: clsx({
          'pt-12 pb-4 z-0': isWebView,
        }),
        onBack: () => {
          if (isWebView) {
            window?.NativeBridge?.onNativeClose?.();
          } else {
            redirectToPlumApp('/benefits-revamp')();
          }
        },
        rightAction: (
          <Link to={LINKS.CREATE_CLAIM} className="hidden md:flex">
            <Button className="w-full rounded-[12px] text-lg py-3 px-4">
              Start a claim
            </Button>
          </Link>
        ),
        sticky: isWebView,
      }}
      footerProps={{
        isSticky: true,
        className: clsx({
          'md:hidden bg-secondary-50': true,
          'bg-white': isWebView,
        }),
        children: (
          <Link to={LINKS.CREATE_CLAIM}>
            <Button className="w-full rounded-[12px] md:text-lg py-3 px-4">
              Start a claim
            </Button>
          </Link>
        ),
      }}
    >
      <div className="flex flex-col md:flex-row justify-between lg:gap-14 md:gap-6 h-full">
        <div
          className={clsx({
            'flex flex-col flex-1 rounded-t-[20px] px-5 gap-4 pb-40 bg-transparent md:px-0 md:py-0 h-full':
              true,
            'bg-white !gap-12 py-8 !pb-24': isWebView,
          })}
        >
          <div
            className={clsx({
              'bg-white flex md:flex-1 rounded-t-[20px] gap-6 md:hidden justify-center':
                true,
              'rounded-2xl p-4 border border-gray-border-200': !isWebView,
              '!p-0 border-none': showSkeletonLoader,
              hidden: !showSkeletonLoader && gmcBenefitId == null,
            })}
          >
            {showSkeletonLoader ? (
              <BlockShimmer className="h-28" />
            ) : gmcBenefitId == null ? null : (
              <>
                {nhStatus?.enabled ? (
                  <ConditionalLink
                    href={isWebView ? null : LINKS.NETWORK_HOSPITALS}
                    onClick={() => {
                      if (isWebView) {
                        window?.NativeBridge?.callDeeplink?.(
                          MOBILE_DEEPLINKS.NETWORK_HOSPITALS,
                        );
                      }
                    }}
                    className={clsx({
                      'basis-1/3': isWebView,
                      'basis-1/2': true,
                    })}
                  >
                    <div className="flex flex-col gap-2 items-center">
                      <CashlessHospital />
                      <p className="text-retail-text-main text-sm text-center font-medium">
                        Find cashless hospitals
                      </p>
                    </div>
                  </ConditionalLink>
                ) : null}
                <ConditionalLink
                  href={
                    isWebView ? null : `${LINKS.HEALTH_IDS}/${gmcBenefitId}`
                  }
                  onClick={() => {
                    if (isWebView) {
                      window?.NativeBridge?.callDeeplink?.(
                        `${MOBILE_DEEPLINKS.BENEFIT_DETAIL_SCREEN}${gmcBenefitId}`,
                      );
                    }
                  }}
                  className={clsx({
                    'basis-1/3': isWebView,
                    'basis-1/2': true,
                  })}
                >
                  <div className="flex flex-col gap-2 items-center text-center">
                    <HealthId />
                    <p className="text-retail-text-main text-sm text-center font-medium">
                      View Health IDs
                    </p>
                  </div>
                </ConditionalLink>
                {isWebView ? (
                  <ConditionalLink
                    href={isWebView ? null : LINKS.COST_OF_AILMENTS}
                    onClick={() => {
                      if (isWebView) {
                        window?.NativeBridge?.callDeeplink?.(
                          MOBILE_DEEPLINKS.COST_OF_AILMENTS,
                        );
                      }
                    }}
                    className="basis-1/3"
                    external
                  >
                    <div className="flex flex-col gap-2 items-center text-center">
                      <CostOfAilment className="w-8 h-8" />
                      <p className="text-retail-text-main text-sm text-center font-medium">
                        Cost of ailment
                      </p>
                    </div>
                  </ConditionalLink>
                ) : null}
              </>
            )}
          </div>

          <div
            className={clsx({
              'grid gap-3 md:gap-5 grid-cols-2': true,
            })}
          >
            <Link
              to={LINKS.ALL_CLAIMS}
              className={clsx({ 'col-span-2': true })}
            >
              <div className="flex items-center gap-4 p-5 border border-gray-border-200 rounded-2xl bg-white">
                <AllClaims />
                <div className="flex flex-col flex-1 text-retail-text-main text-left font-normal gap-1">
                  <p className="font-bold text-xl font-chromatica lg:text-2xl">
                    All claims
                  </p>
                  <p className="text-sm md:text-lg text-mobile-text-hierarchy">
                    View a list of all your claims{' '}
                    <ArrowForward
                      className="inline mb-[2px]"
                      color={get(
                        twConfig,
                        'theme.colors.mobile-text.hierarchy',
                      )}
                    />
                  </p>
                  {isClaimsCountLoading ? (
                    <BlockShimmer className="h-5 w-32 md:h-7 md:w-40" />
                  ) : (
                    <p className="text-sm md:text-lg text-mobile-text-hierarchy">
                      {active ? `${active} active` : ''}
                      {active && drafts ? <> &bull; </> : ''}
                      {drafts ? `${drafts} drafts` : ''}
                      {total === 0 ? 'No active claims' : ''}
                    </p>
                  )}
                </div>
              </div>
            </Link>
            {/* <Link to={`${AppConfig.BASE_PATH}/all`}>
              <div
                className={clsx({
                  'flex flex-col gap-2 p-5 border border-gray-border-200 rounded-2xl text-retail-text-main bg-white':
                    true,
                })}
              >
                <QueryIcon
                  className={clsx({
                    'flex-grow-0 flex-shrink-0': true,
                  })}
                />
                <p
                  className={clsx({
                    'text-left text-sm lg:text-xl': true,
                  })}
                >
                  I have a {isDesktop ? null : <br />} query{' '}
                  <ArrowForward
                    className={clsx({
                      'inline mb-0.5': true,
                    })}
                  />
                </p>
              </div>
            </Link> */}
            {showSkeletonLoader ? (
              <BlockShimmer className="h-40" />
            ) : gmcBenefitId == null ? null : (
              <ConditionalLink
                href={null}
                onClick={() => {
                  if (isWebView) {
                    window?.NativeBridge?.callDeeplink?.(
                      `${
                        MOBILE_DEEPLINKS.SUPPORT_CENTER
                      }&extraInfo=${encodeURIComponent(
                        `route:${LINKS.FILE_REIMBURSEMENT_CLAIM_ARTICLE}`,
                      )}`,
                    );
                  } else {
                    navigateIframe(LINKS.FILE_REIMBURSEMENT_CLAIM_ARTICLE);
                    setIsFloatingWidgetOpen(true);
                  }
                }}
                className="cursor-pointer"
              >
                <div
                  className={clsx({
                    'flex flex-col gap-2 p-5 border border-gray-border-200 rounded-2xl text-retail-text-main bg-white h-full':
                      true,
                  })}
                >
                  <JourneyIcon
                    className={clsx({
                      'flex-grow-0 flex-shrink-0': true,
                    })}
                  />
                  <p
                    className={clsx({
                      'text-left text-sm md:text-xl font-medium': true,
                    })}
                  >
                    How to file <br /> a reimbursement claim{' '}
                    <ArrowForward
                      className={clsx({
                        'inline mb-0.5': true,
                      })}
                    />
                  </p>
                </div>
              </ConditionalLink>
            )}
            {showSkeletonLoader ? (
              <BlockShimmer className="h-40" />
            ) : gmcBenefitId == null ? null : (
              <ConditionalLink
                href={null}
                onClick={() => {
                  if (isWebView) {
                    window?.NativeBridge?.callDeeplink?.(
                      `${
                        MOBILE_DEEPLINKS.SUPPORT_CENTER
                      }&extraInfo=${encodeURIComponent(
                        `route:${LINKS.FILE_CASHLESS_CLAIM_ARTICLE}`,
                      )}`,
                    );
                  } else {
                    navigateIframe(LINKS.FILE_CASHLESS_CLAIM_ARTICLE);
                    setIsFloatingWidgetOpen(true);
                  }
                }}
                className="cursor-pointer"
              >
                <div
                  className={clsx({
                    'flex flex-col gap-2 p-5 border border-gray-border-200 rounded-2xl text-retail-text-main bg-white h-full':
                      true,
                  })}
                >
                  <JourneyIcon
                    className={clsx({
                      'flex-grow-0 flex-shrink-0': true,
                    })}
                  />
                  <p
                    className={clsx({
                      'text-left text-sm md:text-xl font-medium': true,
                    })}
                  >
                    How to file <br /> a cashless claim{' '}
                    <ArrowForward
                      className={clsx({
                        'inline mb-0.5': true,
                      })}
                    />
                  </p>
                </div>
              </ConditionalLink>
            )}
            {isPolicyGPTStatusFetched && areBenefitsFetched ? (
              policyGPTStatus?.status !== true ||
              gmcBenefitId == null ? null : (
                <ConditionalLink
                  href={null}
                  className={clsx({ 'col-span-2 cursor-pointer': true })}
                  onClick={() => {
                    if (isWebView) {
                      window?.NativeBridge?.callDeeplink?.(
                        `${
                          MOBILE_DEEPLINKS.SUPPORT_CENTER
                        }&extraInfo=${encodeURIComponent(
                          `route:${LINKS.POLICY_GPT}`,
                        )}`,
                      );
                    } else {
                      navigateIframe(LINKS.POLICY_GPT);
                      setIsFloatingWidgetOpen(true);
                    }
                  }}
                >
                  <div
                    className={clsx({
                      'flex items-center gap-2 p-5 border border-gray-border-200 rounded-2xl text-retail-text-main bg-white':
                        true,
                    })}
                  >
                    <CheckMyCoverage
                      className={clsx({
                        'flex-grow-0 flex-shrink-0': true,
                      })}
                    />
                    <p
                      className={clsx({
                        'text-left text-sm md:text-lg': true,
                      })}
                    >
                      Ask PolicyGPT to {isDesktop ? null : <br />} check my
                      coverage{' '}
                      <ArrowForward className="mt-auto mb-0.5 -ml-0.5 inline flex-grow-0 flex-shrink-0" />
                    </p>
                  </div>
                </ConditionalLink>
              )
            ) : (
              <BlockShimmer className="h-24 w-full col-span-2" />
            )}
          </div>
        </div>
        <div className="hidden md:flex w-[375px] flex-col rounded-t-[20px] gap-4">
          {showSkeletonLoader ? (
            <BlockShimmer className="h-40 w-full" />
          ) : gmcBenefitId == null ? null : (
            <div className="flex flex-col rounded-2xl gap-8 bg-white border border-gray-border-200 p-8">
              {nhStatus?.enabled ? (
                <ConditionalLink
                  href={isWebView ? null : LINKS.NETWORK_HOSPITALS}
                  onClick={() => {
                    if (isWebView) {
                      window?.NativeBridge?.callDeeplink?.(
                        MOBILE_DEEPLINKS.NETWORK_HOSPITALS,
                      );
                    }
                  }}
                >
                  <div className="flex gap-4 items-center">
                    <CashlessHospital />
                    <p className="text-retail-text-main text-xl flex-1">
                      Find cashless hospitals
                    </p>
                    <ArrowForward
                      className={clsx({
                        'w-4 h-4': true,
                      })}
                    />
                  </div>
                </ConditionalLink>
              ) : null}
              <ConditionalLink
                href={isWebView ? null : `${LINKS.HEALTH_IDS}/${gmcBenefitId}`}
                onClick={() => {
                  if (isWebView) {
                    window?.NativeBridge?.viewHealthID?.();
                  }
                }}
              >
                <div className="flex gap-4 items-center">
                  <HealthId />
                  <p className="text-retail-text-main text-xl flex-1">
                    View Health IDs
                  </p>
                  <ArrowForward
                    className={clsx({
                      'w-4 h-4': true,
                    })}
                  />
                </div>
              </ConditionalLink>
            </div>
          )}
        </div>
      </div>
    </Layout.Main>
  );
};

export default Homepage;
